// src/admin/AdminLayout.jsx — the superadmin console's chrome: the one-shot banners and the (old) tab row.
import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { COLORS, SEMANTIC, FONT_SANS } from '../constants/theme.js';
import { SA_PURGED_BANNER_KEY } from '../auth/tenantPurged.js';

const TABS = [
  { key: 'overview', path: '/admin/overview' },
  { key: 'users', path: '/admin/users' },
  { key: 'tenants', path: '/admin/tenants' },
  { key: 'companies', path: '/admin/companies' },
  { key: 'plans', path: '/admin/plans' },
  { key: 'knowledgeBase', path: '/admin/knowledge-base' },
  { key: 'personas', path: '/admin/personas' },
  { key: 'settings', path: '/admin/context-settings' },
];

function tabStyle(isActive) {
  return {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? COLORS.accent : COLORS.textSecondary,
    textDecoration: 'none',
    borderBottom: `2px solid ${isActive ? COLORS.accent : 'transparent'}`,
    fontFamily: FONT_SANS,
    transition: 'all 0.15s',
  };
}

// One-shot banners set by AuthContext before it navigates into the console. Order = precedence if both are set.
const BANNERS = [
  { storageKey: SA_PURGED_BANNER_KEY, textKey: 'layout.purgedBanner' },  // 410 TENANT_PURGED
  { storageKey: 'sa_blocked_banner', textKey: 'layout.blockedBanner' },  // 403 SUPERADMIN_TENANT_BLOCKED
];

// Pages with their own header: the run pages (full-width) and «Configurazione» (system settings, v219 — PageBand
// with its own tabs). The one-shot banners below still show on them.
const NO_TAB_ROW_PREFIXES = ['/admin/runs', '/admin/settings'];

export default function AdminLayout() {
  const { t } = useTranslation('admin');
  const [bannerKey, setBannerKey] = useState(null);
  const location = useLocation();
  const hideTabs = NO_TAB_ROW_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));

  // Read on EVERY navigation (location.key — react-router mints a new one per navigate, `replace` included): all
  // /admin/* pages share this layout, so a flag set while the superadmin is already inside the console would never
  // be read by a mount-only effect. Shown once, then cleared.
  useEffect(() => {
    try {
      const hit = BANNERS.find(b => sessionStorage.getItem(b.storageKey) === '1');
      if (!hit) return;
      setBannerKey(hit.textKey);
      BANNERS.forEach(b => sessionStorage.removeItem(b.storageKey));
    } catch { /* private mode */ }
  }, [location.key]);

  return (
    <div>
      {bannerKey && (
        <div style={{
          padding: '10px 40px',
          background: SEMANTIC.errore.bg,
          borderBottom: `1px solid ${SEMANTIC.errore.fg}`,
          color: SEMANTIC.errore.fg,
          fontFamily: FONT_SANS,
          fontSize: '13px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}>
          <span>{t(bannerKey)}</span>
          <button
            onClick={() => setBannerKey(null)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: SEMANTIC.errore.fg, fontSize: '15px', fontWeight: 600,
            }}
            aria-label={t('layout.close')}
          >
            ✕
          </button>
        </div>
      )}
      {!hideTabs && (
        <div style={{
          display: 'flex', gap: '4px', padding: '0 40px',
          borderBottom: `1px solid ${COLORS.border}`,
          background: COLORS.surface,
        }}>
          {TABS.map(tab => (
            <NavLink
              key={tab.path}
              to={tab.path}
              style={({ isActive }) => tabStyle(isActive)}
            >
              {t(`layout.tabs.${tab.key}`)}
            </NavLink>
          ))}
        </div>
      )}
      <Outlet />
    </div>
  );
}
